import React from 'react';
import { Modal } from '../ui/Modal';
import { MatchConfig } from './types';
import { useTranslation } from 'react-i18next';

interface ScoreSettingsModalProps {
    isOpen: boolean;
    onClose: () => void;
    config: MatchConfig;
    setConfig: (config: MatchConfig) => void;
    onReset: () => void;
}

export const ScoreSettingsModal: React.FC<ScoreSettingsModalProps> = ({ isOpen, onClose, config, setConfig, onReset }) => {
    const { t } = useTranslation();

    const handleFormatChange = (format: MatchConfig['format']) => {
        setConfig({ ...config, format });
    };

    const toggleGoldenPoint = () => {
        setConfig({ ...config, goldenPoint: !config.goldenPoint });
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={t('matchSettings.title')}>
            <div className="space-y-6">

                {/* Format Selection */}
                <div className="space-y-3">
                    <h3 className="text-sm font-bold text-text-secondary uppercase tracking-lighter">{t('matchSettings.format')}</h3>

                    <button
                        onClick={() => handleFormatChange('1_SET')}
                        className={`w-full p-3 rounded-lg border text-left transition-colors flex items-center justify-between ${config.format === '1_SET' ? 'bg-primary/20 border-primary text-primary' : 'bg-surface-2 border-transparent text-text-primary'}`}
                    >
                        <span>{t('matchSettings.format_1_set')}</span>
                        {config.format === '1_SET' && <span className="material-symbols-outlined">check_circle</span>}
                    </button>

                    <button
                        onClick={() => handleFormatChange('2_SETS')}
                        className={`w-full p-3 rounded-lg border text-left transition-colors flex items-center justify-between ${config.format === '2_SETS' ? 'bg-primary/20 border-primary text-primary' : 'bg-surface-2 border-transparent text-text-primary'}`}
                    >
                        <span>{t('matchSettings.format_2_sets')}</span>
                        {config.format === '2_SETS' && <span className="material-symbols-outlined">check_circle</span>}
                    </button>

                    <button
                        onClick={() => handleFormatChange('2_SETS_SUPER_TIE_BREAK')}
                        className={`w-full p-3 rounded-lg border text-left transition-colors flex items-center justify-between ${config.format === '2_SETS_SUPER_TIE_BREAK' ? 'bg-primary/20 border-primary text-primary' : 'bg-surface-2 border-transparent text-text-primary'}`}
                    >
                        <span>{t('matchSettings.format_2_sets_super_tie_break')}</span>
                        {config.format === '2_SETS_SUPER_TIE_BREAK' && <span className="material-symbols-outlined">check_circle</span>}
                    </button>
                </div>

                {/* Punto de Oro */}
                <div className="flex items-center justify-between bg-surface-2 p-3 rounded-lg">
                    <div>
                        <div className="font-bold text-text-primary">{t('matchSettings.puntoDeOro.title')}</div>
                        <div className="text-xs text-text-secondary">{t('matchSettings.puntoDeOro.desc')}</div>
                    </div>
                    <button
                        onClick={toggleGoldenPoint}
                        className={`w-12 h-7 rounded-full transition-colors relative ${config.goldenPoint ? 'bg-warning' : 'bg-surface-4'}`}
                    >
                        <div className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform ${config.goldenPoint ? 'translate-x-5' : 'translate-x-0'}`} />
                    </button>
                </div>

                {/* Sound & Haptics */}
                <div className="space-y-3">
                    <h3 className="text-sm font-bold text-text-secondary uppercase tracking-lighter">{t('matchSettings.feedback')}</h3>

                    {/* Vibration */}
                    <div className="flex items-center justify-between bg-surface-2 p-3 rounded-lg">
                        <div className="flex items-center gap-3">
                            <span className="material-symbols-outlined text-text-secondary">vibration</span>
                            <span className="font-bold text-text-primary">{t('matchSettings.vibrations')}</span>
                        </div>
                        <button
                            onClick={() => setConfig({ ...config, vibrationEnabled: !config.vibrationEnabled })}
                            className={`w-12 h-7 rounded-full transition-colors relative ${config.vibrationEnabled ? 'bg-primary' : 'bg-surface-4'}`}
                        >
                            <div className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform ${config.vibrationEnabled ? 'translate-x-5' : 'translate-x-0'}`} />
                        </button>
                    </div>

                    {/* Sound */}
                    <div className="flex items-center justify-between bg-surface-2 p-3 rounded-lg">
                        <div className="flex items-center gap-3">
                            <span className="material-symbols-outlined text-text-secondary">volume_up</span>
                            <span className="font-bold text-text-primary">{t('matchSettings.sounds')}</span>
                        </div>
                        <button
                            onClick={() => setConfig({ ...config, soundEnabled: !config.soundEnabled })}
                            className={`w-12 h-7 rounded-full transition-colors relative ${config.soundEnabled ? 'bg-primary' : 'bg-surface-4'}`}
                        >
                            <div className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform ${config.soundEnabled ? 'translate-x-5' : 'translate-x-0'}`} />
                        </button>
                    </div>
                </div>

                {/* Reset Button */}
                <div className="pt-4 border-t border-white/10">
                    <button
                        onClick={() => {
                            if (window.confirm(t('matchSettings.resetConfirm'))) {
                                onReset();
                                onClose();
                            }
                        }}
                        className="w-full py-3 bg-danger/10 text-danger border border-danger/30 rounded-lg font-bold flex items-center justify-center gap-2 hover:bg-danger/20 transition-colors"
                    >
                        {t('matchSettings.resetMatch')}
                    </button>

                    <button
                        onClick={onClose}
                        className="w-full py-3 bg-success text-white rounded-lg font-bold flex items-center justify-center gap-2 shadow-lg hover:brightness-110 transition-all mt-4"
                    >
                        <span className="material-symbols-outlined">check_circle</span>
                        {t('matchSettings.validate', 'Valider')}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

